import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const LOCAL_KEY = 'mysdb_site_secret';

// Public routes that bypass the site gate
const PUBLIC_PATHS = new Set([
  '/privacy',
  '/terms',
  '/sms-optin',
  '/sms-signup',
  '/registration-complete',
  '/about',
  '/reset-password',
  '/forgo-password',
]);

export default function SiteGate({ children }) {
  const { pathname } = useLocation();
  const normalizedPath = pathname.replace(/\/$/, '');
  const isPublic = PUBLIC_PATHS.has(normalizedPath);

  const [secretInput, setSecretInput] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [hasSiteAccess, setHasSiteAccess] = useState(false);

  useEffect(() => {
    let saved = null;

    try {
      saved = sessionStorage.getItem(LOCAL_KEY);
    } catch {
      saved = null;
    }

    if (!saved) {
      setHasSiteAccess(false);
      setVerifying(false);
      return;
    }

    setVerifying(true);

    axios
      .get('/api/site-check', {
        headers: { 'x-site-secret': saved },
      })
      .then(() => {
        axios.defaults.headers.common['x-site-secret'] = saved;
        setHasSiteAccess(true);
      })
      .catch(() => {
        try {
          sessionStorage.removeItem(LOCAL_KEY);
        } catch {
          // ignore
        }

        delete axios.defaults.headers.common['x-site-secret'];
        setHasSiteAccess(false);
      })
      .finally(() => setVerifying(false));
  }, [pathname]);

  // ✅ Public routes render immediately
  if (isPublic) {
    return children;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    try {
      await axios.get('/api/site-check', {
        headers: { 'x-site-secret': secretInput },
      });

      // If we get here, backend accepted it
      try {
        sessionStorage.setItem(LOCAL_KEY, secretInput);
      } catch {
        // ignore if sessionStorage not available
      }

      axios.defaults.headers.common['x-site-secret'] = secretInput;
      setHasSiteAccess(true);
    } catch (err) {
      setError('Incorrect site password. Please try again.');
      setHasSiteAccess(false);
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <h2 style={styles.title}>Loading…</h2>
        </div>
      </div>
    );
  }

  if (!hasSiteAccess) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.card}>
          <h2 style={styles.title}>Private Test Mode</h2>
          <p style={styles.text}>
            This site is currently in private testing. Enter the site password
            to continue.
          </p>

          <form onSubmit={handleSubmit}>
            <input
              type="password"
              value={secretInput}
              onChange={(e) => setSecretInput(e.target.value)}
              placeholder="Site password"
              style={styles.input}
            />
            <button
              type="submit"
              style={styles.button}
              disabled={verifying || !secretInput}
            >
              {verifying ? 'Checking…' : 'Enter'}
            </button>
          </form>

          {error && <p style={styles.error}>{error}</p>}
        </div>
      </div>
    );
  }

  // User hasSiteAccess → show the real app
  return children;
}

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#050b18',
    color: '#fefefe',
  },
  card: {
    background: '#111827',
    borderRadius: 12,
    padding: '24px 28px',
    maxWidth: 400,
    width: '90%',
    boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
    border: '1px solid #374151',
  },
  title: {
    margin: '0 0 12px',
    fontSize: 22,
    fontWeight: 600,
  },
  text: {
    margin: '0 0 18px',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#d1d5db',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    marginBottom: 12,
    borderRadius: 8,
    border: '1px solid #4b5563',
    background: '#020617',
    color: '#f9fafb',
    fontSize: 14,
  },
  button: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    background: '#2563eb',
    color: '#f9fafb',
  },
  error: {
    marginTop: 10,
    color: '#f97373',
    fontSize: 13,
  },
};
